/**
 * 富文本 HTML 渲染预处理
 * 化学式等小图在编辑器里带 width/height，需与文字同行；仅真正大图才独占一行
 */
const BLOCK_MIN_WIDTH = 360
const BLOCK_MIN_HEIGHT = 140
const COS_CUSTOM_HOST = 'cos.shuatibao.com.cn'
const COS_BUCKET_HOST = 'shuti-1408634692.cos.ap-beijing.myqcloud.com'
const HANDOUT_PAGE_BREAK_MARKER = '【分页符】'
const HANDOUT_PAGE_BREAK_HTML = '<div class="handout-page-break" data-handout-page-break="true" style="break-after:page;page-break-after:always;border-top:1px dashed #c8ced8;height:0;margin:18px 0;"></div>'

/** 自定义 COS 域名证书异常时，回退到桶域名 */
export function resolveCosImageUrl(src) {
  if (!src) return src
  return String(src).replace(
    new RegExp(`https?:\\/\\/${COS_CUSTOM_HOST.replace(/\./g, '\\.')}`, 'i'),
    `https://${COS_BUCKET_HOST}`
  )
}

/** 修复 HTML 中所有图片 URL（编辑器加载/保存前使用） */
export function fixCosImageUrlsInHtml(html) {
  if (!html || !/<img/i.test(html)) return html
  if (typeof DOMParser === 'undefined') {
    return html.replace(/<img\b[^>]*>/gi, (tag) => tag
      .replace(/\bsrc=["']([^"']+)["']/gi, (_, src) => `src="${resolveCosImageUrl(src)}"`)
      .replace(/\bdata-href=["']([^"']+)["']/gi, (_, href) => `data-href="${resolveCosImageUrl(href)}"`))
  }
  const doc = new DOMParser().parseFromString(`<div id="__img_root__">${html}</div>`, 'text/html')
  const root = doc.getElementById('__img_root__')
  if (!root) return html
  root.querySelectorAll('img').forEach((img) => {
    let src = (img.getAttribute('src') || img.getAttribute('data-href') || '').trim()
    src = resolveCosImageUrl(src)
    if (!src) return
    if (src.startsWith('//')) src = `https:${src}`
    img.setAttribute('src', src)
    img.setAttribute('data-href', src)
    if (!img.getAttribute('alt')) img.setAttribute('alt', '')
  })
  return root.innerHTML
}

function parseImgStyle(attrs) {
  const styleMatch = attrs.match(/style\s*=\s*"([^"]*)"/i) || attrs.match(/style\s*=\s*'([^']*)'/i)
  if (!styleMatch) return { width: null, height: null }
  const style = styleMatch[1]
  const percentMatch = style.match(/width:\s*([\d.]+)\s*%/i)
  if (percentMatch && parseFloat(percentMatch[1]) >= 50) {
    return { width: Infinity, height: null }
  }
  const widthMatch = style.match(/width:\s*([\d.]+)\s*px/i)
  const heightMatch = style.match(/height:\s*([\d.]+)\s*px/i)
  return {
    width: widthMatch ? parseFloat(widthMatch[1]) : null,
    height: heightMatch ? parseFloat(heightMatch[1]) : null
  }
}

function parseImgSizeFromElement(img) {
  const w = parseFloat(img.getAttribute('width') || '')
  const h = parseFloat(img.getAttribute('height') || '')
  const styleSize = parseImgStyle(` style="${img.getAttribute('style') || ''}" `)
  return {
    width: Number.isFinite(w) && w > 0 ? w : styleSize.width,
    height: Number.isFinite(h) && h > 0 ? h : styleSize.height
  }
}

function parseImgSizeFromTag(tag) {
  const match = String(tag).match(/<img\b([^>]*)>/i)
  const attrs = (match && match[1]) || String(tag)
  const styleSize = parseImgStyle(attrs)
  const widthAttr = attrs.match(/\bwidth\s*=\s*['"]?(\d+)/i)
  const heightAttr = attrs.match(/\bheight\s*=\s*['"]?(\d+)/i)
  return {
    width: widthAttr ? parseFloat(widthAttr[1]) : styleSize.width,
    height: heightAttr ? parseFloat(heightAttr[1]) : styleSize.height
  }
}

function isBlockDiagram({ width, height }) {
  if (width === Infinity) return true
  if (width !== null && width >= BLOCK_MIN_WIDTH) return true
  if (height !== null && height >= BLOCK_MIN_HEIGHT) return true
  return false
}

function paragraphTextWithoutImages(p) {
  if (!p) return ''
  const clone = p.cloneNode(true)
  clone.querySelectorAll('img').forEach((img) => img.remove())
  return clone.textContent.replace(/\s|\u200b/g, '')
}

function isStandaloneImageParagraph(img) {
  const p = img.closest('p')
  if (!p) return false
  return paragraphTextWithoutImages(p).length === 0
}

function isTableImage(img) {
  return !!(img && img.closest && img.closest('td, th, table'))
}

function shouldRenderAsBlockDiagram(img) {
  // 表格内装置图/实验图按块级展示，避免被当成行内小图压成看不见
  if (isTableImage(img)) return true
  if (isBlockDiagram(parseImgSizeFromElement(img))) {
    return true
  }
  return isStandaloneImageParagraph(img)
}

/** 与文字混排的化学式/结构图保持行内；示意图即使与文字同段也按块级展示 */
function shouldInlineImageElement(img) {
  if (shouldRenderAsBlockDiagram(img)) {
    return false
  }
  const p = img.closest('p')
  if (p && paragraphTextWithoutImages(p).length > 0) return true
  return true
}

function patchImageElement(img, forceInline = false) {
  let src = (img.getAttribute('src') || img.getAttribute('data-href') || '').trim()
  src = resolveCosImageUrl(src)
  if (src) {
    if (src.startsWith('//')) src = `https:${src}`
    img.setAttribute('src', src)
    img.setAttribute('data-href', src)
  }
  if (!img.getAttribute('alt')) img.setAttribute('alt', '')
  // 表格内图片禁用懒加载，避免在 overflow/裁剪容器里一直不触发加载
  if (isTableImage(img)) {
    img.setAttribute('loading', 'eager')
  } else if (!img.getAttribute('loading')) {
    img.setAttribute('loading', 'lazy')
  }
  if (!img.getAttribute('decoding')) img.setAttribute('decoding', 'async')
  img.classList.remove('inline-formula', 'block-diagram')
  // 表格内即使 forceInline 也保持可见的示意图尺寸
  const asInline = !isTableImage(img) && (forceInline || shouldInlineImageElement(img))
  img.classList.add(asInline ? 'inline-formula' : 'block-diagram')
}

function applyImageClassesRegex(html, forceInline = false) {
  return html.replace(/<img\b[^>]*>/gi, (tag) => {
    let result = tag
    const srcMatch = result.match(/\bsrc=["']([^"']+)["']/i)
    if (srcMatch) {
      const fixedSrc = resolveCosImageUrl(srcMatch[1])
      if (fixedSrc !== srcMatch[1]) {
        result = result.replace(srcMatch[0], `src="${fixedSrc}"`)
      }
    }
    const hrefMatch = result.match(/\bdata-href=["']([^"']+)["']/i)
    if (hrefMatch) {
      const fixedHref = resolveCosImageUrl(hrefMatch[1])
      if (fixedHref !== hrefMatch[1]) {
        result = result.replace(hrefMatch[0], `data-href="${fixedHref}"`)
      }
    }
    if (!/\bloading=/.test(result)) result = result.replace('<img', '<img loading="lazy"')
    if (!/\bdecoding=/.test(result)) result = result.replace('<img', '<img decoding="async"')

    const match = result.match(/<img\b([^>]*)>/i)
    const attrs = (match && match[1]) || ''
    const size = parseImgSizeFromTag(attrs)
    const imgClass = forceInline || !isBlockDiagram(size) ? 'inline-formula' : 'block-diagram'
    return addClassToTag(result, imgClass)
  })
}

function applyImageClasses(html, options = {}) {
  const forceInline = options.forceInlineImages === true
  if (!html || !/<img/i.test(html)) return html
  if (typeof DOMParser !== 'undefined') {
    try {
      const doc = new DOMParser().parseFromString(`<div id="__img_cls_root__">${html}</div>`, 'text/html')
      const root = doc.getElementById('__img_cls_root__')
      if (root) {
        root.querySelectorAll('img').forEach((img) => patchImageElement(img, forceInline))
        return root.innerHTML
      }
    } catch (e) {
      // fallback below
    }
  }
  return applyImageClassesRegex(html, forceInline)
}

function addClassToTag(tag, className) {
  if (/\bclass\s*=/.test(tag)) {
    return tag
      .replace(/class\s*=\s*"([^"]*)"/i, (_, cls) => `class="${cls} ${className}"`)
      .replace(/class\s*=\s*'([^']*)'/i, (_, cls) => `class='${cls} ${className}'`)
  }
  return tag.replace('<img', `<img class="${className}"`)
}

function isEmptyCellContent(innerHtml) {
  if (!innerHtml) return true
  // 仅含图片/媒体的单元格不算空，否则表格实验图行会被误删
  if (/<(img|video|audio|svg|canvas|iframe)\b/i.test(innerHtml)) return false
  const text = innerHtml
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u200b/g, '')
    .replace(/<span[^>]*>\s*<\/span>/gi, '')
    .replace(/<p>\s*(<br\s*\/?>)?\s*<\/p>/gi, '')
    .replace(/<[^>]+>/g, '')
    .trim()
  return !text
}

function isEmptyTableRow(rowHtml) {
  const cells = rowHtml.match(/<t[dh]\b[^>]*>[\s\S]*?<\/t[dh]>/gi) || []
  if (!cells.length) return true
  return cells.every((cell) => {
    const inner = cell.replace(/^<t[dh][^>]*>/i, '').replace(/<\/t[dh]>$/i, '')
    return isEmptyCellContent(inner)
  })
}

function isEmptyDomCell(cell) {
  if (cell && cell.querySelector && cell.querySelector('img, video, audio, svg, canvas, iframe')) {
    return false
  }
  return isEmptyCellContent(cell.innerHTML)
}

function isShellTable(tableEl) {
  const innerTables = Array.from(tableEl.querySelectorAll('table')).filter((t) => t !== tableEl)
  if (innerTables.length !== 1) return false
  const clone = tableEl.cloneNode(true)
  const innerClone = clone.querySelector('table')
  if (innerClone) innerClone.remove()
  const leftover = clone.innerHTML
    .replace(/<\/?(?:tbody|thead|tfoot|colgroup|col)\b[^>]*>/gi, '')
    .replace(/<tr\b[^>]*>\s*<\/tr>/gi, '')
    .replace(/<t[dh]\b[^>]*>\s*<\/t[dh]>/gi, '')
    .replace(/<p[^>]*>\s*(?:<br\s*\/?>)?\s*<\/p>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u200b/g, '')
    .replace(/<[^>]+>/g, '')
    .trim()
  return !leftover
}

function isPageBreakElement(el) {
  if (!el || !el.getAttribute) return false
  if (el.hasAttribute('data-handout-page-break')) return true
  if (el.classList && (el.classList.contains('handout-page-break') || el.classList.contains('page-break'))) {
    return true
  }
  const style = el.getAttribute('style') || ''
  return /page-break-after\s*:\s*always/i.test(style)
}

function isPageBreakTagHtml(tagHtml) {
  return /(?:data-handout-page-break|class\s*=\s*["'][^"']*\b(?:handout-page-break|page-break)\b|page-break-after\s*:\s*always)/i.test(tagHtml)
}

function cleanEditorHtmlWithDom(html) {
  const doc = new DOMParser().parseFromString(`<div id="__editor_root__">${html}</div>`, 'text/html')
  const root = doc.getElementById('__editor_root__')
  if (!root) return html

  let changed = true
  while (changed) {
    changed = false
    const tables = Array.from(root.querySelectorAll('table'))
    for (const table of tables) {
      if (!table.isConnected || !isShellTable(table)) continue
      const inner = table.querySelector('table')
      if (!inner || inner === table) continue
      table.replaceWith(inner.cloneNode(true))
      changed = true
      break
    }
  }

  root.querySelectorAll('table').forEach((table) => {
    Array.from(table.querySelectorAll('tr')).forEach((row) => {
      // 含图片的行一律保留
      if (row.querySelector('img, video, audio, svg, canvas, iframe')) return
      const cells = Array.from(row.querySelectorAll('th, td'))
      if (!cells.length || cells.every(isEmptyDomCell)) row.remove()
    })
    if (!table.querySelector('tr')) table.remove()
  })

  root.querySelectorAll('p').forEach((p) => {
    if (isPageBreakElement(p)) return
    if (!p.closest('table')) return
    if (!p.textContent.replace(/\s|\u200b/g, '') && !p.querySelector('img')) p.remove()
  })

  return root.innerHTML
}

function stripCellLayoutAttrs(cell) {
  cell.removeAttribute('width')
  if (cell.style) {
    cell.style.removeProperty('width')
    cell.style.removeProperty('min-width')
    cell.style.removeProperty('max-width')
  }
}

function getSpan(cell, attr) {
  const value = parseInt(cell.getAttribute(attr) || '1', 10)
  return Number.isFinite(value) && value > 0 ? value : 1
}

function buildTableLayout(table) {
  const rows = Array.from(table.querySelectorAll('tr'))
  const rowInfos = []
  const pending = new Map()
  let totalCols = 0

  rows.forEach((rowEl) => {
    let col = 0
    const metas = []
    const coveredCells = new Map()

    while (pending.has(col)) col += 1

    Array.from(rowEl.children)
      .filter((cell) => /^(TD|TH)$/i.test(cell.tagName))
      .forEach((cell) => {
        while (pending.has(col)) col += 1
        const colspan = getSpan(cell, 'colspan')
        const rowspan = getSpan(cell, 'rowspan')
        const meta = { cell, startCol: col, colspan, rowspan }
        metas.push(meta)
        for (let i = 0; i < colspan; i += 1) {
          coveredCells.set(col + i, meta)
        }
        col += colspan
      })

    totalCols = Math.max(totalCols, col)
    rowInfos.push({ rowEl, metas, coveredCells })

    const nextPending = new Map()
    pending.forEach((remaining, key) => {
      if (remaining > 1) nextPending.set(key, remaining - 1)
    })
    metas.forEach((meta) => {
      if (meta.rowspan <= 1) return
      for (let i = 0; i < meta.colspan; i += 1) {
        nextPending.set(meta.startCol + i, Math.max(nextPending.get(meta.startCol + i) || 0, meta.rowspan - 1))
      }
    })
    pending.clear()
    nextPending.forEach((value, key) => pending.set(key, value))
  })

  return { rowInfos, totalCols }
}

function removeEmptyTableColumns(root) {
  root.querySelectorAll('table').forEach((table) => {
    // 含图片的表格不做“空列清理”，避免误删仅有图片的列
    if (table.querySelector('img, video, audio, svg, canvas, iframe')) return
    const { rowInfos, totalCols } = buildTableLayout(table)
    if (!rowInfos.length || totalCols <= 1) return

    const contentRows = rowInfos.filter(({ rowEl }) =>
      Array.from(rowEl.children).some((cell) => /^TD$/i.test(cell.tagName)))
    const effectiveRows = contentRows.length ? contentRows : rowInfos
    const removableCols = new Set()
    const startedCols = new Set()

    rowInfos.forEach(({ metas }) => {
      metas.forEach((meta) => startedCols.add(meta.startCol))
    })

    for (let col = totalCols - 1; col >= 0; col -= 1) {
      let hasCell = false
      let allEmpty = true
      for (const rowInfo of effectiveRows) {
        const meta = rowInfo.coveredCells.get(col)
        if (!meta) continue
        hasCell = true
        if (!isEmptyDomCell(meta.cell)) {
          allEmpty = false
          break
        }
      }
      if (hasCell && (allEmpty || !startedCols.has(col))) removableCols.add(col)
    }

    if (!removableCols.size) return

    rowInfos.forEach(({ metas }) => {
      metas.forEach((meta) => {
        let removedCount = 0
        for (let col = meta.startCol; col < meta.startCol + meta.colspan; col += 1) {
          if (removableCols.has(col)) removedCount += 1
        }
        if (!removedCount) return
        const nextColspan = meta.colspan - removedCount
        if (nextColspan <= 0) {
          meta.cell.remove()
          return
        }
        if (nextColspan === 1) {
          meta.cell.removeAttribute('colspan')
        } else {
          meta.cell.setAttribute('colspan', String(nextColspan))
        }
      })
    })
  })
}

function normalizeTablesInDom(root) {
  root.querySelectorAll('colgroup').forEach((el) => el.remove())
  removeEmptyTableColumns(root)
  root.querySelectorAll('table').forEach((table) => {
    table.removeAttribute('width')
    if (table.style) {
      table.style.removeProperty('width')
      table.style.setProperty('table-layout', 'auto')
    }
    table.querySelectorAll('td, th').forEach(stripCellLayoutAttrs)
    table.classList.add('table-fit-content')
    table.classList.remove('col-first-narrow')
  })
}

function cleanEditorHtmlWithRegex(html) {
  let result = html
  result = result.replace(/<tr\b[^>]*>\s*(<table\b[\s\S]*?<\/table>)\s*(?:<p[^>]*>\s*(?:<br\s*\/?>)?\s*<\/p>\s*)*\s*<\/tr>/gi, '$1')
  result = result.replace(/<t[dh]\b[^>]*>\s*(<table\b[\s\S]*?<\/table>)\s*(?:<p[^>]*>\s*(?:<br\s*\/?>)?\s*<\/p>\s*)*\s*<\/t[dh]>/gi, '$1')
  result = result.replace(/<table\b[\s\S]*?<\/table>/gi, (tableBlock) => {
    let table = tableBlock
    let prev
    do {
      prev = table
      table = table.replace(/<tr\b[^>]*>[\s\S]*?<\/tr>/gi, (row) => (isEmptyTableRow(row) ? '' : row))
    } while (table !== prev)
    const tableText = table.replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim()
    return tableText ? table : ''
  })
  return result
}

/** 去掉嵌套空表壳、空表头行、全空表格行及无内容表格（不改变列宽布局，供编辑器使用） */
export function cleanEditorHtml(html) {
  if (!html || !/<table/i.test(html)) return html
  if (typeof DOMParser !== 'undefined') {
    try {
      return cleanEditorHtmlWithDom(html)
    } catch (e) {
      return cleanEditorHtmlWithRegex(html)
    }
  }
  return cleanEditorHtmlWithRegex(html)
}

/** 保存/展示前：按内容自适应列宽 */
function mergeTableStyle(style) {
  const parts = (style || '')
    .split(';')
    .map(s => s.trim())
    .filter(s => s && !/^width\s*:/i.test(s) && !/^table-layout\s*:/i.test(s))
  parts.push('table-layout:auto')
  return parts.join(';')
}

function normalizeTableTagAttrs(attrs) {
  let a = (attrs || '').replace(/\s+width="[^"]*"/gi, '').replace(/\s+width='[^']*'/gi, '')
  if (/style\s*=/i.test(a)) {
    a = a.replace(/style="([^"]*)"/i, (_, style) => ` style="${mergeTableStyle(style)}"`)
      .replace(/style='([^']*)'/i, (_, style) => ` style='${mergeTableStyle(style)}'`)
  } else {
    a += ' style="table-layout:auto"'
  }
  return a
}

function applyAutoTableLayout(html) {
  if (!html || !/<table/i.test(html)) return html
  if (typeof DOMParser !== 'undefined') {
    try {
      const doc = new DOMParser().parseFromString(`<div id="__table_root__">${html}</div>`, 'text/html')
      const root = doc.getElementById('__table_root__')
      if (root) {
        normalizeTablesInDom(root)
        return root.innerHTML
      }
    } catch (e) {
      // fallback to regex below
    }
  }
  return html.replace(/<table\b([^>]*)>/gi, (_, attrs) => `<table${normalizeTableTagAttrs(attrs)}>`)
}

function normalizeHandoutPageBreakMarkers(html) {
  if (!html || !String(html).includes(HANDOUT_PAGE_BREAK_MARKER)) return html
  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(`<div id="__pb_root__">${html}</div>`, 'text/html')
    const root = doc.getElementById('__pb_root__')
    if (root) {
      Array.from(root.querySelectorAll('p,div')).forEach((node) => {
        if (isPageBreakElement(node)) return
        const text = (node.textContent || '').replace(/\s+/g, '').trim()
        if (text !== HANDOUT_PAGE_BREAK_MARKER && text !== '分页符') return
        const replacement = doc.createElement('p')
        replacement.className = 'handout-page-break-token'
        replacement.setAttribute('data-handout-page-break', 'true')
        node.replaceWith(replacement)
      })
      return root.innerHTML
    }
  }
  return String(html)
    .replace(
      /<(p|div)\b(?![^>]*(?:data-handout-page-break|handout-page-break-token|handout-page-break))[^>]*>\s*(?:&nbsp;|\s|<br\s*\/?>)*【分页符】(?:&nbsp;|\s|<br\s*\/?>)*<\/\1>/gi,
      '<p class="handout-page-break-token" data-handout-page-break="true"></p>'
    )
}

/** 保存前清理表格 HTML，保存后按内容自适应列宽 */
export function normalizeTableHtml(html) {
  if (!html) return html
  let result = cleanEditorHtml(html)
  if (!/<table/i.test(result)) return result
  result = result.replace(/<colgroup[\s\S]*?<\/colgroup>/gi, '')
  result = result.replace(/<(td|th)\b([^>]*)>/gi, (match, tag, attrs) => {
    const cleaned = attrs
      .replace(/\s+width="[^"]*"/gi, '')
      .replace(/\s+width='[^']*'/gi, '')
      .replace(/\s+style="[^"]*"/gi, (styleBlock) => {
        const style = styleBlock.slice(7, -1)
        const kept = style
          .split(';')
          .map(s => s.trim())
          .filter(s => s && !/^width\s*:/i.test(s) && !/^min-width\s*:/i.test(s) && !/^max-width\s*:/i.test(s))
          .join(';')
        return kept ? ` style="${kept}"` : ''
      })
      .replace(/\s+style='[^']*'/gi, (styleBlock) => {
        const style = styleBlock.slice(7, -1)
        const kept = style
          .split(';')
          .map(s => s.trim())
          .filter(s => s && !/^width\s*:/i.test(s) && !/^min-width\s*:/i.test(s) && !/^max-width\s*:/i.test(s))
          .join(';')
        return kept ? ` style='${kept}'` : ''
      })
    return `<${tag}${cleaned}>`
  })
  result = result.replace(/<table\b([^>]*)>/gi, (match, attrs) => `<table${normalizeTableTagAttrs(attrs)}>`)
  return applyAutoTableLayout(result)
}

function renderHtmlCore(html, options = {}) {
  const source = options.preservePageBreaks
    ? String(html || '')
    : normalizeHandoutPageBreakMarkers(String(html || ''))
  let result = applyAutoTableLayout(cleanEditorHtml(source))
  result = result.replace(/<table\b([^>]*)>/gi, (match, attrs) => {
    let a = normalizeTableTagAttrs(attrs.replace(/\s+width="auto"/gi, ''))
    if (/\bclass\s*=/.test(a)) {
      a = a.replace(/class\s*=\s*"([^"]*)"/i, 'class="$1 question-table"')
        .replace(/class\s*=\s*'([^']*)'/i, "class='$1 question-table'")
    } else {
      a = ` class="question-table"${a}`
    }
    return `<table${a}>`
  })
  result = result.replace(/<(td|th)\b([^>]*)>/gi, (match, tag, attrs) => {
    let cleaned = attrs.replace(/\s+width="[^"]*"/gi, '').replace(/\s+width='[^']*'/gi, '')
    cleaned = cleaned.replace(/\s+width="auto"/gi, '').replace(/\s+width='auto'/gi, '')
    cleaned = cleaned.replace(/\s+style="[^"]*"/gi, (styleBlock) => {
      const style = styleBlock.slice(7, -1)
      const kept = style
        .split(';')
        .map(s => s.trim())
        .filter(s => s && !/^width\s*:/i.test(s) && !/^min-width\s*:/i.test(s))
        .join(';')
      return kept ? ` style="${kept}"` : ''
    })
    return `<${tag}${cleaned}>`
  })
  return applyImageClasses(result, options)
}

export function renderHtml(html) {
  if (!html) return ''
  return renderHtmlCore(html)
}

/** 选项 HTML：化学式/方程式一律行内展示，避免纯图片选项被当成大图 */
function stripOptionImageDimensions(html) {
  return String(html || '').replace(/<img\b([^>]*)>/gi, (tag, attrs) => {
    let cleaned = attrs
      .replace(/\s+width\s*=\s*['"]?[^'">\s]+['"]?/gi, '')
      .replace(/\s+height\s*=\s*['"]?[^'">\s]+['"]?/gi, '')
    cleaned = cleaned.replace(/\s+style\s*=\s*"([^"]*)"/gi, (_, style) => {
      const kept = style
        .split(';')
        .map(s => s.trim())
        .filter(s => s && !/^width\s*:/i.test(s) && !/^height\s*:/i.test(s))
        .join(';')
      return kept ? ` style="${kept}"` : ''
    })
    cleaned = cleaned.replace(/\s+style\s*=\s*'([^']*)'/gi, (_, style) => {
      const kept = style
        .split(';')
        .map(s => s.trim())
        .filter(s => s && !/^width\s*:/i.test(s) && !/^height\s*:/i.test(s))
        .join(';')
      return kept ? ` style='${kept}'` : ''
    })
    return `<img${cleaned}>`
  })
}

export function renderOptionHtml(html) {
  if (!html) return ''
  return stripOptionImageDimensions(renderHtmlCore(html, { forceInlineImages: true }))
}

/** 题干是否含示意图/装置图等需完整展示的大图（列表预览用，走轻量正则避免卡顿） */
export function hasBlockDiagramInHtml(html) {
  if (!html || !/<img/i.test(html)) return false
  const tags = String(html).match(/<img\b[^>]*>/gi) || []
  if (tags.some((tag) => isBlockDiagram(parseImgSizeFromTag(tag)))) {
    return true
  }
  return /<p\b[^>]*>\s*(?:<br\s*\/?>)?\s*<img\b/i.test(html)
}

/** 讲义预览：保留编辑器分页符结构，避免被替换成 height:0 的占位 div */
export function renderHandoutPreviewHtml(html) {
  if (!html) return ''
  return renderHtmlCore(html, { preservePageBreaks: true })
}
